import {
  FieldConfiguration,
  NOTES_FIELD_INDICES,
  PIC_FIELD_INDICES,
} from "lib/sideLoadedBusinesses/fieldConfigurations";

import { pluralize } from "lib/pluralize";

// Suggestions are stored pre-normalised, so columns are reduced the same way.
export const normaliseColumnName = (columnName: string): string =>
  columnName.toLowerCase().replace(/[^a-z0-9]/g, "");

// Re-anchored to the signature's normalisation, or a matched file maps to nothing.
export const normaliseSourceColumn = (sourceColumn: string): string =>
  sourceColumn.trim().toLowerCase();

// Notes and PICs both gather several columns, into slots numbered their own way.
export const collectsManyColumns = (
  configuration: FieldConfiguration,
): boolean =>
  Boolean(configuration.handleAsNotes || configuration.handleAsProperty);

export const slotIndicesFor = (
  configuration: FieldConfiguration,
): readonly number[] =>
  configuration.handleAsProperty ? PIC_FIELD_INDICES : NOTES_FIELD_INDICES;

// The first target mapped to more columns than it has slots, if any.
export function slotOverflow(
  entries: SideLoadedDataMappingEntry[],
  configurations: FieldConfiguration[],
): { label: string; slotCount: number; overflow: number } | null {
  for (const configuration of configurations.filter(collectsManyColumns)) {
    const slotCount = slotIndicesFor(configuration).length;
    const mapped = (entries || []).filter(
      entry => entry.targetFieldKey === configuration.value,
    ).length;
    if (mapped > slotCount) {
      return {
        label: configuration.label,
        slotCount,
        overflow: mapped - slotCount,
      };
    }
  }
  return null;
}

export function hasDoubledUpTargets(
  entries: SideLoadedDataMappingEntry[],
  configurations: FieldConfiguration[],
): boolean {
  const manyColumnTargets = new Set(
    configurations
      .filter(collectsManyColumns)
      .map(configuration => configuration.value),
  );
  const targets = (entries || [])
    .map(entry => entry.targetFieldKey)
    .filter(target => !manyColumnTargets.has(target));
  return new Set(targets).size !== targets.length;
}

// Rewritten to the file's own spelling; downstream keys off the header as it appears.
export function entriesForColumns(
  entries: SideLoadedDataMappingEntry[],
  sourceColumns: string[],
): SideLoadedDataMappingEntry[] {
  const columnByNormalised = new Map(
    sourceColumns.map(column => [normaliseSourceColumn(column), column]),
  );

  return (entries || []).flatMap(entry => {
    const sourceColumn = columnByNormalised.get(
      normaliseSourceColumn(entry.sourceColumn),
    );

    return sourceColumn ? [{ ...entry, sourceColumn }] : [];
  });
}

// Compared by column rather than by order, since the grid rebuilds entries in file order.
export function mappingEntriesMatch(
  saved: SideLoadedDataMappingEntry[],
  current: SideLoadedDataMappingEntry[],
  sourceColumns: string[],
): boolean {
  const resolvedSaved = entriesForColumns(saved, sourceColumns);
  const resolvedCurrent = entriesForColumns(current, sourceColumns);
  if (resolvedSaved.length !== resolvedCurrent.length) {
    return false;
  }

  const targetByColumn = new Map(
    resolvedSaved.map(entry => [entry.sourceColumn, entry.targetFieldKey]),
  );
  return resolvedCurrent.every(
    entry => targetByColumn.get(entry.sourceColumn) === entry.targetFieldKey,
  );
}

// hasValue stops a blank later column clobbering a populated match. The mapping
// step has no single record, so it passes nothing.
export function guessColumnMap(
  sourceColumns: string[],
  configurations: FieldConfiguration[],
  hasValue?: (sourceColumn: string) => boolean,
): Record<string, string> {
  const columnMap: Record<string, string> = {};

  configurations.forEach(configuration => {
    sourceColumns.forEach(sourceColumn => {
      if (
        configuration.sourceFieldSuggestions.includes(
          normaliseColumnName(sourceColumn),
        ) &&
        (!hasValue || hasValue(sourceColumn))
      ) {
        // Last match wins.
        columnMap[sourceColumn] = configuration.value;
      }
    });
  });

  return columnMap;
}

interface BuildCompareModalInitialValuesProps {
  configurations: FieldConfiguration[];
  sourceColumns: string[];
  rawData: Record<string, string | number>;
  entries?: SideLoadedDataMappingEntry[] | null;
}

// A stored mapping wins over guessing. Entries rather than a column -> target
// map, because one column may feed several targets.
export function buildCompareModalInitialValues({
  configurations,
  sourceColumns,
  rawData,
  entries,
}: BuildCompareModalInitialValuesProps): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  configurations.forEach(configuration => {
    values[`MappableField__${configuration.value}`] = null;
    values[`Value__${configuration.value}`] = "";
  });

  // Role-filtered already, so a key the user cannot see never takes a slot.
  const configurationByValue = configurations.reduce<
    Record<string, FieldConfiguration>
  >((byValue, configuration) => {
    byValue[configuration.value] = configuration;
    return byValue;
  }, {});

  let effectiveEntries = entries;
  if (!effectiveEntries?.length) {
    const guessed = guessColumnMap(
      sourceColumns,
      configurations,
      sourceColumn => Boolean(rawData[sourceColumn]),
    );
    // File order, which decides the notes slots below.
    effectiveEntries = sourceColumns
      .filter(sourceColumn => guessed[sourceColumn])
      .map(sourceColumn => ({
        sourceColumn,
        targetFieldKey: guessed[sourceColumn],
      }));
  }

  const columnsByTarget: Record<string, string[]> = {};

  effectiveEntries.forEach(({ sourceColumn, targetFieldKey }) => {
    const configuration = configurationByValue[targetFieldKey];
    if (!configuration) {
      return;
    }
    // A blank column has no option to select, and would drop silently on submit.
    if (!rawData[sourceColumn]) {
      return;
    }

    if (collectsManyColumns(configuration)) {
      if (!columnsByTarget[configuration.value]) {
        columnsByTarget[configuration.value] = [];
      }
      columnsByTarget[configuration.value].push(sourceColumn);
      return;
    }

    values[`MappableField__${configuration.value}`] = sourceColumn;
    values[`Value__${configuration.value}`] = rawData[sourceColumn];
  });

  configurations.filter(collectsManyColumns).forEach(configuration => {
    const claimedColumns = columnsByTarget[configuration.value];
    if (!claimedColumns?.length) {
      return;
    }
    const slotIndices = slotIndicesFor(configuration);
    // A boolean in a string slot - yup casts it, and the form relies on that.
    values[`MappableField__${configuration.value}`] = true;
    claimedColumns
      .slice(0, slotIndices.length)
      .forEach((sourceColumn, position) => {
        values[
          `MappableField__${configuration.value}__${slotIndices[position]}`
        ] = sourceColumn;
      });
    // Composed by the value editor from the selected columns.
    values[`Value__${configuration.value}`] = "";
  });

  return values;
}

// First column to claim each target wins; a source-major form has no meaning for
// a second row on the same target.
export function guessMappingEntries(
  sourceColumns: string[],
  configurations: FieldConfiguration[],
): SideLoadedDataMappingEntry[] {
  const guessed = guessColumnMap(sourceColumns, configurations);
  const additiveTargets = new Set(
    configurations
      .filter(collectsManyColumns)
      .map(configuration => configuration.value),
  );
  const claimed = new Set<string>();

  return sourceColumns.reduce<SideLoadedDataMappingEntry[]>(
    (entries, sourceColumn) => {
      const targetFieldKey = guessed[sourceColumn];
      if (!targetFieldKey) {
        return entries;
      }
      if (!additiveTargets.has(targetFieldKey)) {
        if (claimed.has(targetFieldKey)) {
          return entries;
        }
        claimed.add(targetFieldKey);
      }
      return entries.concat({ sourceColumn, targetFieldKey });
    },
    [],
  );
}

export function mappedColumnsSummary(
  mappedCount: number,
  totalCount: number,
): string {
  return `${mappedCount} of ${totalCount} columns mapped. Unmapped columns will still be available on each record.`;
}

export function templateUpdateWarning(
  templateName: string,
  usedByCount: number,
): string {
  const usage = usedByCount
    ? `, used by ${usedByCount} other data ${pluralize("set", usedByCount)}`
    : "";
  return `Saving will update the "${templateName}" template${usage}. Choose "Create new template" instead to keep it unchanged.`;
}

// No template means a fresh guess, so switching to "create new" never inherits
// a previous template's mapping.
export function entriesForTemplate(
  template: SideLoadedDataMapping | undefined,
  sourceColumns: string[],
  configurations: FieldConfiguration[],
): SideLoadedDataMappingEntry[] {
  return template
    ? entriesForColumns(template.entries, sourceColumns)
    : guessMappingEntries(sourceColumns, configurations);
}
